// Procedural EKG waveform generator. Returns y value (0 center, +/- amplitude)
// for a given time t (seconds) and pattern.

const twoPI = Math.PI * 2;

function sinusBeat(phase, pr = 0, qrsWidth = 0.08, stShift = 0) {
  // Build a stylized PQRST per cycle (phase in 0..1)
  // P wave
  const p = Math.exp(-Math.pow((phase - 0.18) / 0.03, 2)) * 0.18;
  // Q
  const q = -Math.exp(-Math.pow((phase - 0.33) / 0.012, 2)) * 0.25;
  // R (big spike)
  const r = Math.exp(-Math.pow((phase - 0.36) / 0.014, 2)) * 1.2;
  // S
  const s = -Math.exp(-Math.pow((phase - 0.40) / 0.014, 2)) * 0.45;
  // ST segment baseline shift (for STEMI)
  const stBand = phase > 0.42 && phase < 0.5 ? stShift : 0;
  // T wave
  const t = Math.exp(-Math.pow((phase - 0.58) / 0.06, 2)) * 0.35;
  return p + q + r + s + stBand + t;
}

export function sampleEkg(t, params) {
  const { pattern = "sinus", rate = 75 } = params || {};
  const beatPeriod = 60 / Math.max(1, rate);

  switch (pattern) {
    case "sinus": {
      const phase = (t % beatPeriod) / beatPeriod;
      return sinusBeat(phase);
    }
    case "afib": {
      // Irregular R-R intervals with fibrillatory baseline, no P
      const seed = Math.floor(t / 0.3);
      const jitter = ((Math.sin(seed * 12.9898) * 43758.5453) % 1 + 1) % 1;
      const period = beatPeriod * (0.6 + jitter * 0.9);
      const phase = (t % period) / period;
      const fib = (Math.sin(t * 35) + Math.sin(t * 53) + Math.sin(t * 71)) * 0.04;
      // Suppress P wave
      let v = sinusBeat(phase);
      v -= Math.exp(-Math.pow((phase - 0.18) / 0.03, 2)) * 0.18;
      return v + fib;
    }
    case "aflutter": {
      // 2:1 flutter: atrial rate = 2× ventricular rate (~300/min). Classic sawtooth F waves
      // (lead II): slow linear downslope over ~86% of the F cycle, sharp upstroke over ~14%,
      // no isoelectric baseline. QRS is offset onto the downslope so it doesn't mask the peak.
      const phase = (t % beatPeriod) / beatPeriod;
      const fPhase = (phase * 2 + 0.35) % 1;
      const RISE = 0.14;
      let saw;
      if (fPhase < 1 - RISE) saw = 0.5 - fPhase / (1 - RISE);
      else {
        const u = (fPhase - (1 - RISE)) / RISE;
        saw = -0.5 + u * u * (3 - 2 * u);
      }
      saw *= 0.4;
      // Narrow QRS riding on the F waves; T wave is buried in the flutter baseline
      const q = -Math.exp(-Math.pow((phase - 0.47) / 0.012, 2)) * 0.15;
      const r = Math.exp(-Math.pow((phase - 0.5) / 0.013, 2)) * 1.1;
      const s = -Math.exp(-Math.pow((phase - 0.535) / 0.013, 2)) * 0.35;
      return saw + q + r + s;
    }
    case "svt": {
      // Regular narrow-complex tachycardia >150 (SkillStat-style strip): tall thin R spikes,
      // small rounded T between beats, and a clear pointed P riding right in front of each QRS.
      const phase = (t % beatPeriod) / beatPeriod;
      const p = Math.exp(-Math.abs(phase - 0.44) / 0.018) * 0.32;
      const q = 0;
      const r = Math.exp(-Math.pow((phase - 0.5) / 0.016, 2)) * 1.25;
      const s = -Math.exp(-Math.pow((phase - 0.527) / 0.011, 2)) * 0.28;
      const tw = Math.exp(-Math.pow((phase - 0.74) / 0.065, 2)) * 0.2;
      return p + q + r + s + tw;
    }
    case "vtach": {
      const phase = (t % beatPeriod) / beatPeriod;
      // Wide, monomorphic, no P, no T
      const r = Math.sin(phase * twoPI) * 1.1;
      const widen = Math.exp(-Math.pow((phase - 0.5) / 0.25, 2)) * 1.1;
      return (r * 0.3 + widen * (phase < 0.5 ? 1 : -0.8)) * 0.9;
    }
    case "vfib": {
      // Chaotic noise
      const n =
        Math.sin(t * 23.1) * 0.6 +
        Math.sin(t * 41.7) * 0.45 +
        Math.sin(t * 67.3) * 0.3 +
        Math.sin(t * 97.9) * 0.2;
      return n * 0.9;
    }
    case "asystole": {
      return (Math.sin(t * 1.3) * 0.005);
    }
    case "av1": {
      // Normal sinus with wider PR (shift P earlier so PR appears long)
      const phase = (t % beatPeriod) / beatPeriod;
      let v = sinusBeat(phase);
      // Add extra P offset back
      v -= Math.exp(-Math.pow((phase - 0.18) / 0.03, 2)) * 0.18;
      v += Math.exp(-Math.pow((phase - 0.10) / 0.03, 2)) * 0.18;
      return v;
    }
    case "wenckebach": {
      // Group of 4 beats, 4th is dropped QRS with lone P
      const groupPeriod = beatPeriod * 4;
      const groupPhase = (t % groupPeriod) / groupPeriod; // 0..1
      const beatIndex = Math.floor(groupPhase * 4); // 0..3
      const subPhase = (groupPhase * 4) % 1;
      if (beatIndex === 3) {
        // Just a P wave then flat
        return Math.exp(-Math.pow((subPhase - 0.18) / 0.03, 2)) * 0.18;
      }
      return sinusBeat(subPhase);
    }
    case "mobitz2": {
      // Every 3rd beat is dropped (constant PR on conducted)
      const groupPeriod = beatPeriod * 3;
      const groupPhase = (t % groupPeriod) / groupPeriod;
      const beatIndex = Math.floor(groupPhase * 3);
      const subPhase = (groupPhase * 3) % 1;
      if (beatIndex === 2) {
        return Math.exp(-Math.pow((subPhase - 0.18) / 0.03, 2)) * 0.18;
      }
      return sinusBeat(subPhase);
    }
    case "av3": {
      // Independent atrial and ventricular rates
      const atrialPeriod = 60 / 80;
      const ventPeriod = 60 / 35;
      const ap = (t % atrialPeriod) / atrialPeriod;
      const vp = (t % ventPeriod) / ventPeriod;
      const p = Math.exp(-Math.pow((ap - 0.3) / 0.03, 2)) * 0.18;
      const qrs =
        -Math.exp(-Math.pow((vp - 0.33) / 0.015, 2)) * 0.3 +
        Math.exp(-Math.pow((vp - 0.37) / 0.02, 2)) * 1.1 +
        -Math.exp(-Math.pow((vp - 0.42) / 0.02, 2)) * 0.4 +
        Math.exp(-Math.pow((vp - 0.6) / 0.07, 2)) * 0.3;
      return p + qrs;
    }
    case "lbbb":
    case "rbbb": {
      const phase = (t % beatPeriod) / beatPeriod;
      let v = sinusBeat(phase);
      // Widen QRS by adding an extra lobe
      const extra = Math.exp(-Math.pow((phase - 0.44) / 0.03, 2)) * 0.7;
      v += pattern === "rbbb" ? extra : -extra;
      return v;
    }
    case "stemi_anterior":
    case "stemi_inferior":
    case "stemi_lateral": {
      const phase = (t % beatPeriod) / beatPeriod;
      // Elevated ST segment
      return sinusBeat(phase, 0, 0.08, 0.35);
    }
    case "torsades": {
      // Polymorphic VT with twisting amplitude
      const period = 0.3;
      const phase = (t % period) / period;
      const amp = Math.sin(t * 1.2) * 1.1;
      const r = Math.exp(-Math.pow((phase - 0.5) / 0.1, 2));
      return r * amp;
    }
    default:
      return 0;
  }
}

// ---------------------------------------------------------------------------
// 12-lead support. Standard 4x3 grid order (row-major).
// ---------------------------------------------------------------------------
export const LEADS = ["I", "aVR", "V1", "V4", "II", "aVL", "V2", "V5", "III", "aVF", "V3", "V6"];

const LEAD_GAIN = {
  I: { p: 0.9, q: 0.3, r: 0.8, s: 0.2, t: 0.9 },
  II: { p: 1.1, q: 0.4, r: 1.2, s: 0.3, t: 1.1 },
  III: { p: 0.6, q: 0.5, r: 0.6, s: 0.5, t: 0.6 },
  aVR: { p: -0.8, q: 0, r: -0.9, s: 0, t: -0.8 },
  aVL: { p: 0.5, q: 0.3, r: 0.6, s: 0.4, t: 0.5 },
  aVF: { p: 0.9, q: 0.4, r: 0.9, s: 0.4, t: 0.9 },
  V1: { p: 0.4, q: 0, r: 0.25, s: 1.1, t: -0.4 },
  V2: { p: 0.4, q: 0, r: 0.45, s: 1.0, t: 0.7 },
  V3: { p: 0.4, q: 0.1, r: 0.75, s: 0.7, t: 0.9 },
  V4: { p: 0.5, q: 0.2, r: 1.3, s: 0.4, t: 1.0 },
  V5: { p: 0.6, q: 0.3, r: 1.2, s: 0.2, t: 0.9 },
  V6: { p: 0.6, q: 0.3, r: 1.0, s: 0.15, t: 0.8 },
};

const ST_MAP = {
  stemi_anterior: { V1: 0.3, V2: 0.45, V3: 0.45, V4: 0.35, II: -0.12, III: -0.15, aVF: -0.12 },
  stemi_inferior: { II: 0.35, III: 0.45, aVF: 0.4, I: -0.15, aVL: -0.22 },
  stemi_lateral: { I: 0.3, aVL: 0.35, V5: 0.35, V6: 0.35, II: -0.1, III: -0.18, aVF: -0.14 },
};

const LATERAL = ["I", "aVL", "V5", "V6"];
const BEAT_PATTERNS = ["sinus", "av1", "lbbb", "rbbb", "stemi_anterior", "stemi_inferior", "stemi_lateral"];

export function stLeadsFor(pattern) {
  return Object.entries(ST_MAP[pattern] || {}).filter(([, v]) => v > 0).map(([k]) => k);
}

const g = (phase, c, w) => Math.exp(-Math.pow((phase - c) / w, 2));

function leadBeat(phase, lead, pattern) {
  const L = LEAD_GAIN[lead];
  const st = (ST_MAP[pattern] || {})[lead] || 0;
  let v = 0;
  if (pattern !== "svt") {
    const pCenter = pattern === "av1" ? 0.1 : 0.18;
    v += g(phase, pCenter, 0.03) * 0.18 * L.p;
  }
  if (pattern !== "lbbb" || !LATERAL.includes(lead)) v -= g(phase, 0.33, 0.012) * 0.25 * L.q;
  v += g(phase, 0.36, 0.014) * 1.2 * L.r;
  v -= g(phase, 0.4, 0.014) * 0.45 * L.s;
  if (pattern === "rbbb") {
    if (lead === "V1" || lead === "V2") v += g(phase, 0.45, 0.025) * 0.9;
    if (LATERAL.includes(lead)) v -= g(phase, 0.45, 0.03) * 0.5;
  }
  if (pattern === "lbbb") {
    if (lead === "V1" || lead === "V2") v -= g(phase, 0.41, 0.035) * 0.9;
    if (LATERAL.includes(lead)) v += g(phase, 0.42, 0.03) * 0.8 * L.r;
  }
  if (st) {
    if (phase > 0.41 && phase < 0.52) v += st;
    else if (phase >= 0.52 && phase < 0.56) v += st * (1 - (phase - 0.52) / 0.04);
  }
  const tGain = pattern === "lbbb" && LATERAL.includes(lead) ? -0.8 : st > 0 ? 1.5 : 1;
  v += g(phase, 0.58, 0.06) * 0.35 * L.t * tGain;
  return v;
}

export function sampleLead(t, params, lead) {
  const { pattern = "sinus", rate = 75 } = params || {};
  if (BEAT_PATTERNS.includes(pattern)) {
    const beatPeriod = 60 / Math.max(1, rate);
    const phase = (t % beatPeriod) / beatPeriod;
    return leadBeat(phase, lead, pattern);
  }
  const scale = lead === "aVR" ? -0.8 : lead === "V1" ? 0.7 : lead === "III" || lead === "aVL" ? 0.6 : 1;
  return sampleEkg(t, params) * scale;
}

// Detect QRS edges to trigger beeps. Takes previous and current y and threshold.
export function isQrsPeak(prev, curr, next) {
  return curr > 0.55 && curr > prev && curr >= next;
}

// Pulseless rhythms: no perfusing beat regardless of electrical activity.
export const isPulseless = (params) =>
  !!params?.pulseless || params?.pattern === "asystole" || params?.pattern === "vfib";

// Monitor-style pulse readout. Pulseless rhythms read 0 even if there is electrical activity (PEA).
export function hrLabel(params) {
  const { pattern, rate } = params || {};
  if (isPulseless(params) || !rate) return "0";
  if (pattern === "afib" || pattern === "torsades") return `~${rate}`;
  if (pattern === "wenckebach") return `${Math.round(rate * 0.75)}`;
  if (pattern === "mobitz2") return `${Math.round(rate * 2 / 3)}`;
  if (pattern === "av3") return "35";
  return `${rate}`;
}
